import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Book.css';
// import './BHSearchBar.css';

// ─── Constantes ────────────────────────────────────────────────────────────────

const BASE_URL = 'https://bibliotecahumana.urosario.edu.co';
const MIN_QUERY_LEN = 2;
const MAX_OBJETOS_RESULTS = 6;
const MAX_CAPS_RESULTS = 6;
const BATCH_SIZE = 5; // peticiones paralelas al indexar capítulos

const TIPO_LABELS = {
  1: 'LIBRO',
  2: '', // TIENEN MODIFICACIONES - PERSONALIZACIONES
  3: '',
  4: 'PODCAST',
  5: 'DOCUMENTALES',
  6: 'SERIE',
};


// ─── Utilidades ────────────────────────────────────────────────────────────────
 
/**
 * Normaliza texto: minúsculas + sin acentos.
 * Permite buscar "comunicacion" y hallar "Comunicación".
 */
const normalizeText = (text) =>
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
 
/**
 * Divide el query en tokens individuales (palabras), filtrando
 * los que no alcanzan la longitud mínima útil.
 * Ej: "maria palacios" → ["maria", "palacios"]
 *     "  felipe  " → ["felipe"]
 */
const tokenize = (query) =>
  normalizeText(query)
    .split(/\s+/)
    .filter(t => t.length >= 2);
 
/**
 * Comprueba que TODOS los tokens del query aparezcan en el haystack
 * (en cualquier orden y posición).
 * Ej: matchAllTokens("Maria Teresa Palacios", "maria palacios") → true
 *     matchAllTokens("Felipe González", "maria palacios")       → false
 */
const matchAllTokens = (haystack, tokens) => {
  const normHay = normalizeText(haystack);
  return tokens.every(token => normHay.includes(token));
};
 
/**
 * Resalta todas las ocurrencias de cada token en `text`.
 * Construye un array de fragmentos JSX marcando los segmentos coincidentes.
 * Ej: "Maria Teresa Palacios" buscando "maria palacios" →
 *     [<span>Maria</span>, " Teresa ", <span>Palacios</span>]
 */
const highlightMatch = (text, query) => {
  if (!text || !query) return text || '';
 
  const tokens = tokenize(query);
  if (tokens.length === 0) return text;
 
  // Construir un regex que encuentre cualquiera de los tokens (orden no importa)
  const escaped = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const normText = normalizeText(text);
 
  // Recopilar todos los rangos [start, end] que coinciden con algún token
  const ranges = [];
  for (const pattern of escaped) {
    const re = new RegExp(pattern, 'g');
    let m;
    while ((m = re.exec(normText)) !== null) {
      ranges.push([m.index, m.index + m[0].length]);
    }
  }
 
  if (ranges.length === 0) return text;
 
  // Ordenar y fusionar rangos solapados
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [s, e] of ranges) {
    if (merged.length && s <= merged[merged.length - 1][1]) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    } else {
      merged.push([s, e]);
    }
  }
 
  // Reconstruir el texto original con los spans de resaltado
  const parts = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (cursor < s) parts.push(text.slice(cursor, s));
    parts.push(
      <span key={s} className="bh-search-highlight">{text.slice(s, e)}</span>
    );
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
 
  return <>{parts}</>;
};
 
// ─── Íconos SVG inline ─────────────────────────────────────────────────────────
 
const IconSearch = () => (
  <svg className="bh-search-icon" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
 
const IconSpinner = () => (
  <svg className="bh-search-spinner" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83
             M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);
 
const IconPlay = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);
 
const IconBook = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);
 
const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
 
// ─── Componente Principal ──────────────────────────────────────────────────────
 
function BHSearchBar() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isIndexing, setIsIndexing] = useState(true);
  const [objetos, setObjetos] = useState([]);
  const [chapters, setChapters] = useState({});   // { [idObjeto]: Chapter[] }
  const [results, setResults] = useState({ objetos: [], capitulos: [] });
 
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
 
  // ── 1. Carga inicial de objetos ──────────────────────────────────────────────
  useEffect(() => {
    fetch(`${BASE_URL}/api/objetos`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        const active = data.filter(o => o.status === '1' || o.status === '2');
        setObjetos(active);
        indexChapters(active);
      })
      .catch(err => {
        console.error('[BHSearchBar] Error al cargar objetos:', err);
        setIsIndexing(false);
      });
  }, []);
 
  // ── 2. Indexado de capítulos en background (por lotes) ───────────────────────
  const indexChapters = useCallback(async (items) => {
    setIsIndexing(true);
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const batch = items.slice(i, i + BATCH_SIZE);
      const batchMap = {};
      await Promise.allSettled(
        batch.map(async (item) => {
          try {
            const r = await fetch(`${BASE_URL}/api/libro/${item.idObjeto}/capitulos`);
            if (!r.ok) return;
            const caps = await r.json();
            if (Array.isArray(caps) && caps.length > 0) {
              batchMap[item.idObjeto] = caps;
            }
          } catch (_) { /* red o parse error: se ignora */ }
        })
      );
      // Actualiza el estado progresivamente para que la búsqueda mejore mientras indexa
      setChapters(prev => ({ ...prev, ...batchMap }));
    }
    setIsIndexing(false);
  }, []);
 
  // ── 3. Motor de búsqueda ─────────────────────────────────────────────────────
  useEffect(() => {
    // Dividir el query en tokens individuales: "maria palacios" → ["maria", "palacios"]
    // Esto permite encontrar "Maria Teresa Palacios Sanabria" aunque las palabras
    // no sean contiguas en el texto original.
    const tokens = tokenize(query);
 
    if (tokens.length === 0 || normalizeText(query).length < MIN_QUERY_LEN) {
      setResults({ objetos: [], capitulos: [] });
      setIsOpen(false);
      return;
    }
 
    // Búsqueda en objetos (libros / series):
    // todos los tokens deben aparecer en algún campo del objeto
    const matchedObjetos = objetos
      .filter(obj => {
        const hay = [obj.Titulo, obj.descripcion, obj.keywords, obj.categoria].join(' ');
        return matchAllTokens(hay, tokens);
      })
      .slice(0, MAX_OBJETOS_RESULTS);
 
    // Búsqueda en capítulos:
    // todos los tokens deben aparecer en algún campo del capítulo
    const matchedCaps = [];
    for (const [parentId, caps] of Object.entries(chapters)) {
      const parent = objetos.find(o => o.idObjeto === Number(parentId));
      for (const cap of caps) {
        const hay = [cap.title, cap.autor, cap.keywords].join(' ');
        if (matchAllTokens(hay, tokens)) {
          matchedCaps.push({ ...cap, _parent: parent });
        }
      }
      if (matchedCaps.length >= MAX_CAPS_RESULTS) break;
    }
 
    const newResults = {
      objetos: matchedObjetos,
      capitulos: matchedCaps.slice(0, MAX_CAPS_RESULTS),
    };
 
    setResults(newResults);
    setIsOpen(
      newResults.objetos.length > 0 || newResults.capitulos.length > 0 || tokens.length > 0
    );
  }, [query, objetos, chapters]);
 
  // ── 4. Cerrar dropdown al hacer clic fuera ───────────────────────────────────
  useEffect(() => {
    const handle = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);
 
  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleClear = () => {
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };
 
  const handleSelect = () => {
    setQuery('');
    setIsOpen(false);
  };
 
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };
 
  // ── Variables derivadas ───────────────────────────────────────────────────────
  const totalResults = results.objetos.length + results.capitulos.length;
  const showEmpty = isOpen && totalResults === 0 && normalizeText(query).length >= MIN_QUERY_LEN;
  const showDropdown = (isOpen && totalResults > 0) || showEmpty;
 
  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="bh-search-wrapper" ref={wrapperRef}>
 
      {/* ── Input ── */}
      <div className={`bh-search-input-container${showDropdown ? ' bh-search-input-container--open' : ''}`}>
        <IconSearch />
 
        <input
          ref={inputRef}
          type="search"
          className="bh-search-input"
          placeholder="Buscar libros, series, capítulos…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => totalResults > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          aria-label="Buscar en Biblioteca Humana"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          autoComplete="off"
          spellCheck="false"
        />
 
        {isIndexing && (
          <span className="bh-search-indexing-indicator" title="Indexando contenido…">
            <IconSpinner />
          </span>
        )}
 
        {query && (
          <button
            className="bh-search-clear-btn"
            onClick={handleClear}
            aria-label="Limpiar búsqueda"
            tabIndex={0}
          >
            <IconClose />
          </button>
        )}
      </div>
 
      {/* ── Dropdown ── */}
      {showDropdown && (
        <div className="bh-search-dropdown" role="listbox" aria-label="Resultados de búsqueda">
 
          {/* ── Grupo: Libros y Series ── */}
          {results.objetos.length > 0 && (
            <div className="bh-search-group">
              <p className="bh-search-group-label">Libros y Series</p>
              {results.objetos.map(obj => (
                <a
                  key={obj.idObjeto}
                  href={obj.url_libro}
                  className="bh-search-item"
                  role="option"
                  onClick={handleSelect}
                >
                  {/* Thumbnail */}
                  <div className="bh-search-item-thumb">
                    {obj.url_caratula ? (
                      <img
                        src={`${BASE_URL}${obj.url_caratula}`}
                        alt=""
                        loading="lazy"
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="bh-search-item-thumb-placeholder">
                        <IconBook />
                      </div>
                    )}
                  </div>
 
                  {/* Info */}
                  <div className="bh-search-item-info">
                    <span className="bh-search-item-title">
                      {highlightMatch(obj.Titulo, query)}
                    </span>
                    <span className="bh-search-item-meta">
                      <span className={`bh-search-badge bh-search-badge--${obj.idTipo === 6 ? 'serie' : 'libro'}`}>
                        {TIPO_LABELS[obj.idTipo] || 'Recurso'}
                      </span>
                      {obj.annio && (
                        <span className="bh-search-item-year">{obj.annio}</span>
                      )}
                    </span>
                    {obj.descripcion && (
                      <span className="bh-search-item-desc">
                        {obj.descripcion.slice(0, 90).trimEnd()}…
                      </span>
                    )}
                  </div>
 
                  {/* Flecha */}
                  <span className="bh-search-item-arrow" aria-hidden="true">›</span>
                </a>
              ))}
            </div>
          )}
 
          {/* ── Grupo: Capítulos ── */}
          {results.capitulos.length > 0 && (
            <div className="bh-search-group">
              <p className="bh-search-group-label">Capítulos</p>
              {results.capitulos.map(cap => (
                <a
                  key={cap.idObjeto}
                  href={cap._parent?.url_libro || '#'}
                  className="bh-search-item bh-search-item--chapter"
                  role="option"
                  onClick={handleSelect}
                >
                  {/* Ícono play */}
                  <div className="bh-search-item-thumb bh-search-item-thumb--chapter">
                    <IconPlay />
                  </div>
 
                  {/* Info */}
                  <div className="bh-search-item-info">
                    <span className="bh-search-item-title">
                      {highlightMatch(cap.title, query)}
                    </span>
                    <span className="bh-search-item-meta">
                      <span className="bh-search-badge bh-search-badge--chapter">Capítulo</span>
                      {cap.duracion && (
                        <span className="bh-search-item-year">{cap.duracion}</span>
                      )}
                    </span>
                    {cap.autor && (
                      <span className="bh-search-item-desc">
                        {highlightMatch(cap.autor, query)}
                      </span>
                    )}
                    {cap._parent?.Titulo && (
                      <span className="bh-search-item-parent">
                        en: {cap._parent.Titulo}
                      </span>
                    )}
                  </div>
 
                  <span className="bh-search-item-arrow" aria-hidden="true">›</span>
                </a>
              ))}
            </div>
          )}
 
          {/* ── Sin resultados ── */}
          {showEmpty && (
            <div className="bh-search-empty">
              <IconSearch />
              <p>
                Sin resultados para <strong>"{query}"</strong>
              </p>
              <span>Intenta con otras palabras clave o el título del autor</span>
            </div>
          )}
 
          {/* ── Pie del dropdown ── */}
          {totalResults > 0 && (
            <div className="bh-search-footer">
              <span>{totalResults} resultado{totalResults !== 1 ? 's' : ''}</span>
              {isIndexing && (
                <span className="bh-search-footer-indexing">
                  <IconSpinner /> Indexando más contenido…
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
 
export default BHSearchBar;